import { gql } from '@apollo/client'
import { pathOr } from 'ramda'
import React from 'react'

import { serviceOrdersClient } from '../../../../utils/apollo/api'
import { useModalData } from '../../../../components/modals/ModalsContext'
import { usePermalinkToken } from '../../../../utils/session/permalinkToken'
import { useQuery } from '../../../../utils/apollo/_helpers/useQuery'
import StatePresenter from '../../../../components/helpers/StatePresenter'
import { mergeBulkServiceOrders } from './mergeBulkServiceOrders'

// Máximo de atividades permitidas em uma execução/edição em massa.
export const MAX_BULK_SERVICE_ORDERS = 30

const Context = React.createContext([])
const { Provider } = Context
export const useServiceOrderState = () => React.useContext(Context)
export const useServiceOrder = () => useServiceOrderState()?.serviceOrder
export const useQuestions = () => pathOr([], ['checklist', 'questions'], useServiceOrder())
export const useReplies = () => pathOr([], ['replies'], useServiceOrder())
export const useIsBulkExecution = () => !!useServiceOrder()?.bulk
export const useBulkServiceOrderIds = () => useServiceOrder()?.bulk?.ids || []
// A lista "crua" das atividades selecionadas (cada uma com seu próprio
// status/replies), usada pela grade de execução em massa. Diferente de
// `useServiceOrder()`, que expõe um registro único e representativo (usado
// pelo cabeçalho/rodapé do modal).
export const useBulkServiceOrders = () => useServiceOrderState()?.bulkServiceOrders

const EXECUTION_QUERY = gql`
  query ServiceOrder($id: ID, $executionToken: String, $filter: ServiceOrderFilter) {
    serviceOrder(id: $id, executionToken: $executionToken, filter: $filter) {
      id
      yearlyId
      description
      insertedAt
      scheduling
      name
      fieldValues {
        id
        value
        fieldId
        field {
          type
          id
          name
          configuration
          options {
            id
            name
            reference
            ordination
          }
        }
      }
      documents {
        id
        src
        type
        description
      }
      status {
        id
        name
      }
      events {
        id
        status {
          id
          name
        }
      }
      place {
        id
        name
        resume
        fieldValues {
          id
          value
          fieldId
          field {
            id
            name
          }
        }
      }
      asset {
        id
        name
      }
      user {
        id
        name
        fieldValues {
          id
          value
          fieldId
          field {
            id
            name
          }
        }
      }
      checklistId
      replies {
        id
        value
        notes
        questionId
        blockIndex
        question {
          id
          type {
            id
            name
          }
        }
        documents {
          id
          src
          type
          description
        }
      }
      checklist {
        id
        name
        questions {
          id
          name
          reference
          parentReference
          data
          displayConditions
          ordination
          categoryOrdination
          required
          allowNull
          data
          documents {
            id
            src
            type
            description
          }
          description
          photosEnabled
          photosRequired
          notesEnabled
          notesRequired
          defaultValue
          options {
            id
            name
            reference
            ordination
          }
          masks {
            id
            name
            pattern
          }
          category {
            id
            name
          }
          type {
            id
            name
          }
        }
      }
    }
  }
`
const PUBLIC_QUERY = gql`
  query ServiceOrder($id: ID, $permalinkToken: String) {
    serviceOrder(id: $id, permalinkToken: $permalinkToken) {
      id
      yearlyId
      description
      insertedAt
      scheduling
      name
      fieldValues {
        id
        value
        fieldId
        field {
          type
          id
          name
          configuration
          options {
            id
            name
            reference
            ordination
          }
        }
      }
      documents {
        id
        src
        type
        description
      }
      status {
        id
        name
      }
      events {
        id
        status {
          id
          name
        }
      }
      place {
        id
        name
        resume
        fieldValues {
          id
          value
          fieldId
          field {
            id
            name
          }
        }
      }
      asset {
        id
        name
      }
      user {
        id
        name
        fieldValues {
          id
          value
          fieldId
          field {
            id
            name
          }
        }
      }
      checklistId
      replies {
        id
        value
        notes
        questionId
        blockIndex
        question {
          id
          type {
            id
            name
          }
        }
        documents {
          id
          src
          type
          description
        }
      }
      checklist {
        id
        name
        questions {
          id
          name
          reference
          parentReference
          data
          displayConditions
          ordination
          categoryOrdination
          required
          allowNull
          data
          documents {
            id
            src
            type
            description
          }
          description
          photosEnabled
          photosRequired
          notesEnabled
          notesRequired
          defaultValue
          options {
            id
            name
            reference
            ordination
          }
          masks {
            id
            name
            pattern
          }
          category {
            id
            name
          }
          type {
            id
            name
          }
        }
      }
    }
  }
`

const isUUID = value => {
  if (!value) return false
  const UUID_V4 = new RegExp(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi)
  return value.match(UUID_V4)
}

// Mesma seleção de campos usada por EXECUTION_QUERY/PUBLIC_QUERY, só que para
// várias atividades de uma vez (execução/edição em massa).
const BULK_EXECUTION_QUERY = gql`
  query BulkServiceOrdersForExecution($ids: [Int], $limit: Int) {
    serviceOrders(filter: { ids: $ids, ignoreSegments: true }, limit: $limit) {
      id
      yearlyId
      description
      insertedAt
      scheduling
      name
      status {
        id
        name
      }
      place {
        id
        name
        resume
      }
      asset {
        id
        name
      }
      user {
        id
        name
      }
      checklistId
      replies {
        id
        value
        notes
        questionId
        blockIndex
        question {
          id
          type {
            id
            name
          }
        }
        documents {
          id
          src
          type
          description
        }
      }
      checklist {
        id
        name
        allowBulkExecution
        questions {
          id
          name
          reference
          parentReference
          data
          displayConditions
          ordination
          categoryOrdination
          required
          allowNull
          documents {
            id
            src
            type
            description
          }
          description
          photosEnabled
          photosRequired
          notesEnabled
          notesRequired
          defaultValue
          options {
            id
            name
            reference
            ordination
          }
          masks {
            id
            name
            pattern
          }
          category {
            id
            name
          }
          type {
            id
            name
          }
        }
      }
    }
  }
`

const useBulkModalIds = modalId => (Array.isArray(modalId?.bulkServiceOrderIds) ? modalId.bulkServiceOrderIds : null)

// `true` quando NENHUMA das atividades carregadas tem um checklist com
// `allowBulkExecution: false` — usado para desabilitar o "Enviar" da grade e
// mostrar o motivo, adicionalmente à checagem de "mesmo questionário" (feita
// antes de abrir esta tela, em `utils/actions/serviceOrders/bulkExecution.js`).
export const useBulkExecutionAllowed = () => {
  const bulkServiceOrders = useBulkServiceOrders()
  if (!bulkServiceOrders?.length) return true
  return bulkServiceOrders.every(so => so.checklist?.allowBulkExecution !== false)
}

const ServiceOrderLoader = ({ children, id }) => {
  const modalId = useModalData()
  const bulkIds = useBulkModalIds(modalId)
  const isBulk = !!bulkIds
  // Fix to avoid losing id when alert modal is open
  id = React.useRef(id || modalId).current
  const token = usePermalinkToken()

  const query = isBulk ? BULK_EXECUTION_QUERY : isUUID(token) ? EXECUTION_QUERY : PUBLIC_QUERY
  const variables = isBulk
    ? { ids: bulkIds, limit: MAX_BULK_SERVICE_ORDERS }
    : isUUID(token)
    ? { id, executionToken: token, filter: { ignoreSegments: true } }
    : { id, permalinkToken: token }

  const { data, ...state } = useQuery(query, {
    variables,
    client: serviceOrdersClient,
    fetchPolicy: 'no-cache'
  })

  const value = React.useMemo(
    () =>
      isBulk
        ? { serviceOrder: mergeBulkServiceOrders(data?.serviceOrders), bulkServiceOrders: data?.serviceOrders }
        : data,
    [isBulk, data]
  )

  return (
    <StatePresenter {...state}>
      <Provider value={value}>{children}</Provider>
    </StatePresenter>
  )
}

export default ServiceOrderLoader
